import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Search, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

const Row = ({ label, mr, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-3">
    <span className="text-sm text-slate-500">{label} <span className="text-xs text-slate-400">/ {mr}</span></span>
    <span className="text-sm font-medium text-slate-800">{children}</span>
  </div>
);

export default function PublicStatus() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setData(null); setHistory([]);
    try {
      const { data: res } = await api.get("/public/status", { params: { query } });
      setData(res);
      const { data: h } = await api.get(`/public/status/history/${res.beneficiary_id}`);
      setHistory(h);
    } catch (e2) { setError(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#f4f6fa]">
      <header className="flex h-16 items-center gap-3 bg-[#0f2545] px-4 text-white">
        <div className="grid h-9 w-9 place-items-center rounded bg-amber-400 font-bold text-[#0f2545]">स्व</div>
        <div className="leading-tight">
          <div className="font-serif text-sm font-semibold sm:text-base">Know Your Swadhar Status</div>
          <div className="text-[11px] text-slate-300">स्वाधार योजना स्थिती तपासा · जिल्हा लातूर</div>
        </div>
        <Link to="/login" data-testid="staff-login-link" className="ml-auto flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20">
          Staff Login
        </Link>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 p-4 sm:p-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Check your application status</h1>
          <p className="mt-1 text-sm text-slate-600">अर्ज क्रमांक (Application ID) किंवा लाभार्थी क्रमांक (Beneficiary ID) टाकून तुमची स्वाधार योजना स्थिती पहा.</p>
        </div>

        <form onSubmit={search} className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <input data-testid="public-status-input" value={query} onChange={(e) => setQuery(e.target.value)} required minLength={6}
            placeholder="e.g. NLATHSC251026343 or SWD000004"
            className="min-w-[240px] flex-1 rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:border-[#0f2545] focus:outline-none" />
          <button data-testid="public-status-search" disabled={busy} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Check Status
          </button>
        </form>

        {error && <div data-testid="public-status-error" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}

        {data && (
          <div className="space-y-4" data-testid="public-status-result">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-xs text-slate-500">{data.beneficiary_id} · {data.application_number}</div>
                  <div className="font-serif text-2xl font-semibold text-[#0f2545]">{data.name}</div>
                  <div className="text-xs text-slate-500">{data.taluka} · {data.location_type} · {data.academic_year} · {data.application_type}</div>
                </div>
                <StatusBadge value={data.eligibility_status} testId="public-eligibility-badge" />
              </div>
              {data.eligibility_reason && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <span className="font-medium">कारण / Reason: </span>{data.eligibility_reason}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <Row label="College / Institution" mr="महाविद्यालय">{data.institution || "—"}</Row>
              <Row label="Course / Year" mr="अभ्यासक्रम">{`${data.course || ""} ${data.class_year || ""}`.trim() || "—"}</Row>
              <Row label="Bank Status" mr="बँक स्थिती"><StatusBadge value={data.bank_status} /></Row>
              <Row label="Bank Account" mr="बँक खाते">{data.bank_account_masked || "Not submitted"}</Row>
              <Row label="First Installment" mr="पहिला हप्ता">
                <span className="flex items-center gap-2"><StatusBadge value={data.first_installment.status} />
                  {data.first_installment.amount ? `₹${data.first_installment.amount}` : ""} {data.first_installment.payment_date}</span>
              </Row>
              <Row label="Second Installment" mr="दुसरा हप्ता">
                <span className="flex items-center gap-2"><StatusBadge value={data.second_installment.status} />
                  {data.second_installment.amount ? `₹${data.second_installment.amount}` : ""} {data.second_installment.payment_date}</span>
              </Row>
              <Row label="Attendance" mr="उपस्थिती">
                <span className="flex items-center gap-2"><StatusBadge value={data.attendance_status} />{data.attendance_percentage ? `${data.attendance_percentage}%` : ""}</span>
              </Row>
              <div className="pt-3 text-xs text-slate-400">Last updated: {data.last_updated ? new Date(data.last_updated).toLocaleString("en-IN") : "—"}</div>
            </div>

            {history.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="public-status-history">
                <h3 className="mb-3 text-base font-semibold text-[#0f2545] md:text-lg">Status History / स्थिती इतिहास</h3>
                <ol className="space-y-2">
                  {history.map((h) => (
                    <li key={`${h.created_at}-${h.new_status}`} className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-2 text-sm">
                      {h.previous_status && <><StatusBadge value={h.previous_status} /><span className="text-slate-400">→</span></>}
                      <StatusBadge value={h.new_status} />
                      <span className="text-xs text-slate-600">{h.reason}</span>
                      <span className="ml-auto text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString("en-IN")}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck size={14} /> Names and account numbers are partially masked for privacy. For corrections contact the Social Welfare Office, Latur.
            </div>
          </div>
        )}

        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#0f2545] underline"><ArrowLeft size={14} /> Staff login</Link>
      </main>
    </div>
  );
}
